import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import {
  createOrUpdateClass,
  createOrUpdateClassFn,
  getClass,
  getClassFn,
  getMyClasses,
  getMyClassesFn,
  getMyManagingClasses,
  getMyManagingClassesFn,
  queryClasses,
  queryClassesFn,
  setMyGroup,
  setMyGroupFn,
  updateParticipants,
} from 'store/pdp/pdpSlice';
import LoadingCircleIndicator from 'components/LoadingCircleIndicator';
import { RouteComponentProps, navigate } from '@reach/router';
import messages from './Messages';
import ClassViewer from './ClassViewer';
import { convertDigitToProperFormat, isANumber } from 'utils/number';
import {
  blue,
  exactWhite,
  lightBlue,
  lightGray,
  lightGreen,
  lightGreenSolid,
} from 'utils/style/colors';
import { Card } from '../CompanyPermissionSchemes/styles';
import {
  ClassListContainer,
  ClassListContainerHeader,
  CreateClassContainer,
  EditorWrapper,
  SearchUserContainer,
  TermsWrapper,
} from '../PDPTerms/style';
import ButtonGroup from 'components/ButtonGroup';
import ClassMemberViewer from './ClassMemberViewer';
import { getAccessToken } from 'api/Utils';
import {
  ClassProps,
  CompanyPDPState,
  Participant,
  // ParticipantPhasePropsOverride,
  PDPClass,
} from 'store/pdp/types';
import {
  ICompany,
  ICompanyMember,
  ICompanyProviders,
  ICompanyRole,
} from 'store/companies/companiesTypes';
import { IProviderItemView, ITags } from 'store/providers/providerTypes';
import { appDispatch, IThisUser, IUsers, RootState } from 'store/store';
import {
  ISurveyItem,
  ISurveyResponseView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { isCompanyOwnerOrSupport } from 'store/companies/companiesUtils';
import {
  canIFill,
  canIFillFn,
  updateSurvey,
  updateSurveyFn,
} from 'store/survey/surveySlice';
import {
  item_createNewSurvey,
  item_createNewSurveyFn,
} from 'store/providers/providerSlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import Input from '../../../../components/Input';
import moment from 'moment-jalaali';
import { ClassListCol } from './style';
import { useSafeNavigate } from '../../../../context/SafeNavigate/SafeNavigateProvider';
import Button from '../../../../components/Button/Button';
import APopover from '../../../../components/APopover/APopover';
import { languageDirection } from '../../../../i18n';
import Select, { IOption } from '../../../../components/Select/Select';
import AddUserToCoursesModal from './AddUserToCoursesModal';
import Notify from '../../../../components/Notify/Notify';
import { getMyClassInfo } from './ClassListView';
import notify from '../../../../components/Notify';
import Icon from '../../../../components/Icon';
import i18n from 'i18next';
import UserMixpanel from 'utils/mixpanel/MixpanelClass';
import { ClassTemplateEditor } from './ClassTemplateEditor';
import HoverIcon from '../../../../components/HoverIcon';

interface IOwnProps {
  classId: string;
  termId?: string;
  pdpState: CompanyPDPState;
  companyId: string;
  pdpId?: string;
  children?: React.ReactNode;
}

interface StateToProps {
  relatedProviders: ICompanyProviders;
  company: ICompany;
  isPDPAdmin: boolean;
  tags: ITags['data'];
  thisUser: IThisUser;
  users: IUsers['map'];
  companyMembers?: { [key: number]: ICompanyMember };
  token?: string;
}

interface DispatchToProps {
  canIFill: (
    response: ISurveyResponseView,
    surveyItemData: ISurveyItem
  ) => ReturnType<typeof canIFillFn>;
  queryClasses: (
    pdpId: number,
    startDate?: number,
    endDate?: number,
    isTemplate?: boolean,
    termId?: number,
    showDeleted?: boolean
  ) => ReturnType<typeof queryClassesFn>;
  createOrUpdateClass: (
    clazz: PDPClass,
    pdpId: number,
    skipConflictCheck?: boolean
  ) => ReturnType<typeof createOrUpdateClassFn>;
  getClass: (classId: number, pdpId: number) => ReturnType<typeof getClassFn>;
  getMyClasses: (
    termId: number,
    pdpId: number
  ) => ReturnType<typeof getMyClassesFn>;
  getMyManaginClasses: (
    termId: number,
    pdpId: number
  ) => ReturnType<typeof getMyManagingClassesFn>;
  createProviderSurvey: (
    item: ISurveyItem
  ) => ReturnType<typeof item_createNewSurveyFn>;
  updateSurvey: (
    surveyData: ISurveyView,
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof updateSurveyFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  setMyGroup: (
    pdpId: number,
    clazzId: number,
    group: string
  ) => ReturnType<typeof setMyGroupFn>;
}

type IComponentProps = StateToProps &
  IOwnProps &
  DispatchToProps &
  RouteComponentProps;

const ClassManager: React.FC<IComponentProps> = (props) => {
  const { notifyUnsavedChanges, navigateWithConfirmation } = useSafeNavigate();
  const pdpID = useMemo(() => Number(props.pdpId), [props.pdpId]);
  const classId = useMemo(() => {
    if (props.classId) {
      return Number(props.classId);
    } else if (window.location.href?.split('/class/')?.at(1)) {
      const classId = Number(window.location.href.split('/class/').at(1));
      return classId;
    }
    return 0;
  }, [props.classId, window.location.href]);
  const showCalendar = props.classId === 'calendar';
  const clazz =
    showCalendar || !classId ? undefined : props.pdpState?.classes?.[classId];
  const [loading, setLoading] = useState(new Set<string>());
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedTopicToCreateNewClass, setSelectedTopicToCreateNewClass] =
    useState<IOption[]>([]);
  const [createClassLoading, setCreateClassLoading] = useState<boolean>(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    number | undefined
  >();
  const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>();
  const [enableAddUserToCourses, setEnableAddUserToCourses] =
    useState<boolean>(false);

  const addLoading = useCallback((scope: string) => {
    setLoading((prev) => new Set(prev).add(scope));
  }, []);

  const removeLoading = useCallback((scope: string) => {
    setLoading((prev) => {
      const newSet = new Set(prev);
      newSet.delete(scope);
      return newSet;
    });
  }, []);

  const handleCreateNewClass = async () => {
    const { company, termId } = props;
    setCreateClassLoading(true);
    const properties: ClassProps = {
      classDocumentHomeId: null as any,
      autoApproveProps: {} as any,
      classGroups: [],
      phaseProps: company.pdpPanels.find((el) => el.id === pdpID)!.properties
        .classPhaseDefaultProps!,
    };

    for (const t of selectedTopicToCreateNewClass) {
      try {
        const clazz: PDPClass = {
          id: undefined as any,
          topicId: Number(t!.value),
          properties: properties,
          template: false,
          participants: [],
          relatedTermIds: [Number(termId)],
        };
        await appDispatch(createOrUpdateClass({ clazz, pdpId: pdpID }));
      } catch (e) {
        notify(messages.errorCreatingClass(t.label), 'error');
      }
    }
    setSelectedTopicToCreateNewClass([]);
    Notify(messages.courseAddedSuccessfully, 'successful');
    setCreateClassLoading(false);
  };

  const isAdmin = useMemo(
    () =>
      props.isPDPAdmin ||
      clazz?.managerUserId === Number(props.thisUser.userId),
    [props.isPDPAdmin, clazz?.managerUserId, props.thisUser.userId]
  );
  useEffect(() => {
    if (!clazz && classId) {
      addLoading('current');
      props.getClass(classId, pdpID).finally(() => {
        removeLoading('current');
      });
    } else removeLoading('current');
  }, [classId]);

  const termId = Number(props.termId);
  useEffect(() => {
    addLoading('currentTermClassList');
    if (isANumber(termId)) {
      if (props.isPDPAdmin)
        props
          .queryClasses(pdpID, undefined, undefined, false, termId, showDeleted)
          .finally(() => {
            removeLoading('currentTermClassList');
          });
      else {
        Promise.all([props.getMyClasses(termId, pdpID)]).finally(() => {
          props.getMyManaginClasses(termId, pdpID).finally(() => {
            removeLoading('currentTermClassList');
          });
        });
      }
    } else if (classId) {
      props.getClass(classId, pdpID).finally(() => {
        removeLoading('currentTermClassList');
      });
    }
  }, [props.termId, showDeleted]);

  const classPageInPath =
    window.location.hash && window.location.hash.length
      ? window.location.hash.substr(1)
      : undefined;
  const [page, setPage] = useState(
    classPageInPath ?? (isAdmin ? 'members' : 'view')
  );

  const classPages = [
    {
      title: messages.editClass,
      link: 'edit',
    },
    {
      title: messages.Participants,
      link: 'members',
    },
    {
      title: messages.previewClass,
      link: 'preview',
    },
  ];
  const classesInTerm = useMemo(() => {
    return Object.values(props.pdpState?.classes || {}).filter(
      (c) =>
        (termId ? c.relatedTermIds.includes(termId) : c.id === classId) &&
        (showDeleted || !c.deleted)
    );
  }, [
    props.pdpState?.classes,
    termId,
    showDeleted,
    ...(termId ? [] : [classId]),
  ]);

  const startOfWeek = moment().startOf('week').valueOf();
  const endOfWeek = moment().endOf('week').valueOf();
  const startOfDay = moment().startOf('day').valueOf();
  const endOfDay = moment().endOf('day').valueOf();
  let foundCurrentWeek = false;
  let foundCurrentDay = false;
  const currentDay = useRef<HTMLElement | null>(null);
  const selectedCourse = useRef<HTMLElement | null>(null);

  const termRoleIds = useMemo(() => {
    return props.pdpState?.terms?.[termId]?.term?.properties.targetRoleIds;
  }, [props.pdpState?.terms, termId]);

  const termUserRoleId = useMemo(() => {
    if (!termRoleIds) return [];
    const userIds = new Set<number>();
    Object.values(props.company.roles).forEach((role: ICompanyRole) => {
      if (termRoleIds.includes(role.id)) {
        role.users.forEach((user) => userIds.add(user.userId));
      }
    });
    return Array.from(userIds);
  }, [props.company.roles, termRoleIds]);

  const participantsUserIdList = useMemo(() => {
    const userIds = new Set<number>();
    classesInTerm.forEach((item: PDPClass) => {
      Object.values(item.participants).forEach((p) => userIds.add(p.userId));
    });
    return Array.from(userIds);
  }, [classesInTerm]);

  const usersOptions: IOption[] = useMemo(() => {
    const allUserIds = Array.from(
      new Set([...participantsUserIdList, ...termUserRoleId])
    );
    return allUserIds.map((userId) => {
      const courseCount = classesInTerm.filter((clazz) =>
        Object.values(clazz.participants).some(
          (p) => p.userId === userId && p.participantProps.status !== 'Dropped'
        )
      ).length;
      const employeeId = props.company.members[userId]?.employeeId;
      return {
        label:
          `${props?.users?.[userId]?.name}${employeeId ? ` - ${employeeId}` : ''}` +
          (courseCount
            ? ` (${messages.termsEn(convertDigitToProperFormat(courseCount))})`
            : ''),
        value: userId,
      };
    });
  }, [
    participantsUserIdList,
    termUserRoleId,
    classesInTerm,
    props.company.members,
    props.users,
  ]);

  const termUserOptions: IOption[] = useMemo(() => {
    return termUserRoleId.map((userId) => ({
      label: `${props?.users?.[userId]?.name} ${props.company.members[userId]?.employeeId ? `- ${props.company.members[userId].employeeId}` : ''}`,
      value: userId,
    }));
  }, [termUserRoleId, props.users, props.company.members]);

  useEffect(() => {
    if (selectedCourse.current)
      selectedCourse.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    else if (currentDay.current)
      currentDay.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  }, [termId, ...Object.keys(props.pdpState?.classes || {}).sort()]);

  const toCreateOptions = useMemo(() => {
    return Object.values(props.pdpState?.topics || {})
      .filter((topic) => topic.status === 'Exists')
      .filter(
        (topic) =>
          !classesInTerm.find(
            (it) => it.topicId === Number(topic.id) && !it.deleted
          )
      )
      .map((topic) => ({
        label: topic.properties.name,
        value: topic.id,
      })) as IOption[];
  }, [props.pdpState?.topics, classesInTerm]);

  const topicOptions: IOption[] = useMemo(() => {
    const topicsWithClasses = new Set<number>();
    classesInTerm.forEach((clazz) => {
      if (!clazz.deleted) {
        topicsWithClasses.add(clazz.topicId);
      }
    });

    return Array.from(topicsWithClasses)
      .map((topicId) => {
        const topic = props.pdpState?.topics?.[topicId];
        return {
          label: topic?.properties?.name || topicId.toString(),
          value: topicId,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [classesInTerm, props.pdpState?.topics]);

  const filteredClasses = useMemo(() => {
    let filtered = classesInTerm;

    if (selectedParticipantId) {
      filtered = filtered.filter((it) =>
        Object.values(it.participants)
          .map((el) =>
            el.participantProps.status !== 'Dropped' ? el.userId : 0
          )
          .includes(selectedParticipantId)
      );
    }

    if (selectedTopicId) {
      filtered = filtered.filter((it) => it.topicId === selectedTopicId);
    }

    return filtered.sort((a, b) => (a.startDate || 0) - (b.startDate || 0));
  }, [classesInTerm, selectedParticipantId, selectedTopicId]);

  const addTrackUserCourseViewed = () => {
    const mx = UserMixpanel.getInstance(Number(props.thisUser.userId));
    mx.handleAddNumberOfViewedCourses();
  };

  if (loading.has('current')) return <LoadingCircleIndicator />;

  return (
    <TermsWrapper>
      <AddUserToCoursesModal
        isOpen={enableAddUserToCourses}
        onClose={() => setEnableAddUserToCourses(false)}
        termUserOptions={termUserOptions}
        pdpState={props.pdpState}
        company={props.company}
        tags={props.tags}
        users={props.users}
        pdpId={pdpID}
        termId={termId}
        classesInTerm={classesInTerm}
      />
      <ClassListContainer
        className={clazz ? 'hide-on-mobile' : ''}
        style={{ flex: !clazz ? 1 : 0 }}
      >
        <ClassListContainerHeader>
          <SearchUserContainer>
            <APopover
              target={
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    borderRadius: '1rem',
                    backgroundColor: lightGray,
                    padding: '5px',
                  }}
                >
                  <HoverIcon type={'user'} color={blue}></HoverIcon>
                  <span>
                    {selectedParticipantId
                      ? props.users[selectedParticipantId].name
                      : ''}
                  </span>
                </div>
              }
              arrow
              curveBorder
            >
              <div>
                <Select
                  placeholder={messages.filterParticipants}
                  options={usersOptions}
                  values={
                    usersOptions.filter(
                      (option: IOption) =>
                        option.value === selectedParticipantId
                    ) || undefined
                  }
                  onChangeValues={(options) =>
                    setSelectedParticipantId(
                      options.length ? Number(options[0].value) : undefined
                    )
                  }
                  multiChoice={false}
                />
              </div>
            </APopover>
            <APopover
              target={
                <div
                  style={{
                    border: `1px solid ${lightGray}`,
                    borderRadius: '1rem',
                    backgroundColor: lightGray,
                    padding: '5px',
                  }}
                >
                  <HoverIcon type={'tag'} color={blue}></HoverIcon>
                  <span>
                    {selectedTopicId
                      ? props.pdpState?.topics?.[selectedTopicId]?.properties
                          ?.name
                      : ''}
                  </span>
                </div>
              }
              arrow
              curveBorder
            >
              <div>
                <Select
                  placeholder={messages.filterByTopic}
                  options={topicOptions}
                  values={
                    topicOptions.filter(
                      (option: IOption) => option.value === selectedTopicId
                    ) || undefined
                  }
                  onChangeValues={(options) =>
                    setSelectedTopicId(
                      options.length ? Number(options[0].value) : undefined
                    )
                  }
                  multiChoice={false}
                />
              </div>
            </APopover>
          </SearchUserContainer>
          <span style={{ flex: 1 }} />
          {!clazz && !showCalendar && (
            <CreateClassContainer>
              <APopover
                placement={
                  languageDirection() === 'rtl' ? 'left-start' : 'right-start'
                }
                target={
                  <Button
                    onClick={() => {
                      // do nothing, only open up the popover
                    }}
                  >
                    <Icon type="plus" />
                    {messages.createAClass}
                  </Button>
                }
              >
                <div
                  style={{
                    backgroundColor: exactWhite,
                    width: '18rem',
                    padding: '0.5rem 1rem',
                  }}
                  className="flex flex-col gap-5"
                >
                  <Select
                    options={toCreateOptions}
                    values={selectedTopicToCreateNewClass}
                    onChangeValues={(values) =>
                      setSelectedTopicToCreateNewClass(values)
                    }
                    disabled={loading.has('currentTermClassList')}
                    label={messages.selectTitle}
                    multiChoice={true}
                  />
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Button
                      onClick={() =>
                        setSelectedTopicToCreateNewClass(toCreateOptions)
                      }
                      disabled={loading.has('currentTermClassList')}
                    >
                      {messages.selectAll}
                    </Button>
                    <Button
                      disabled={!selectedTopicToCreateNewClass.length}
                      onClick={handleCreateNewClass}
                      loading={createClassLoading}
                      color="blue"
                    >
                      {messages.createClass}
                    </Button>
                  </div>
                </div>
              </APopover>
              <Button onClick={() => setEnableAddUserToCourses(true)}>
                <Icon type="user-plus" />
                {messages.addMemberToCourses}
              </Button>
            </CreateClassContainer>
          )}
        </ClassListContainerHeader>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <table style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <ClassListCol />
            </colgroup>
            <thead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'var(--exact-white)',
              }}
            >
              <tr>
                <th>
                  {(() => {
                    const selected = !clazz && !showCalendar;
                    return (
                      <Card
                        key={'overview'}
                        onClick={(e: any) => {
                          if (!selected) {
                            navigateWithConfirmation?.('../class', e);
                          }
                        }}
                        style={{
                          backgroundColor: selected
                            ? lightGreenSolid
                            : undefined,
                          cursor: selected ? 'default' : 'pointer',
                          borderRadius: '10px',
                        }}
                        margin={'0'}
                      >
                        <span>{messages.calendar}</span>
                      </Card>
                    );
                  })()}
                </th>
                {!clazz && (
                  <>
                    <th>{messages.classStartEnd}</th>
                    <th>{messages.teacherName}</th>
                    <th>{messages.holdType}</th>
                  </>
                )}
              </tr>
            </thead>
          </table>
          <div
            style={{
              flex: 1,
              overflow: 'hidden auto',
              direction: i18n.dir() === 'ltr' ? 'rtl' : 'ltr',
            }}
          >
            <table
              style={{
                tableLayout: 'fixed',
                width: 'calc(100%-8px)',
                direction: i18n.dir(),
              }}
            >
              <colgroup>
                <ClassListCol />
              </colgroup>
              <tbody>
                {filteredClasses.map((it) => {
                  const selected = it.id === clazz?.id;
                  const isPdpAdmin = props.isPDPAdmin;
                  const thisUserId = +props.thisUser.userId;
                  const { holdType, duration, inWeek, inCurrentDay } =
                    getMyClassInfo(
                      isPdpAdmin,
                      it,
                      thisUserId,
                      startOfWeek,
                      endOfWeek,
                      startOfDay,
                      endOfDay
                    );
                  return (
                    <tr
                      ref={(e) => {
                        if (!foundCurrentDay && inCurrentDay) {
                          foundCurrentDay = true;
                          currentDay.current = e;
                        }
                        if (!foundCurrentDay && !foundCurrentWeek && inWeek) {
                          foundCurrentWeek = true;
                          currentDay.current = e;
                        }
                        if (selected) selectedCourse.current = e;
                      }}
                      key={it.id}
                      style={{
                        background: clazz
                          ? undefined
                          : inCurrentDay
                            ? lightBlue
                            : inWeek
                              ? lightGreen
                              : undefined,
                        ...(selected
                          ? { position: 'sticky', top: 0, bottom: 0 }
                          : {}),
                      }}
                    >
                      <td>
                        <Card
                          key={it.id}
                          onClick={(e: any) => {
                            addTrackUserCourseViewed();
                            if (
                              !clazz &&
                              !showCalendar &&
                              navigateWithConfirmation
                            ) {
                              navigateWithConfirmation(`./class/${it.id}`, e);
                            } else if (
                              (it.id !== clazz?.id || showCalendar) &&
                              navigateWithConfirmation
                            )
                              navigateWithConfirmation(`../class/${it.id}`, e);
                          }}
                          style={{
                            backgroundColor: selected
                              ? lightGreenSolid
                              : undefined,
                            cursor: selected ? 'default' : 'pointer',
                            transform: selected ? 'translate(-1rem, 0)' : '',
                            borderRadius: selected ? '0 10px 10px 0' : '10px',
                            margin: 0,
                          }}
                        >
                          <span
                            style={{
                              transition: 'all 700ms',
                              margin: selected ? '0 auto 10px' : '0',
                            }}
                          >
                            {
                              props.pdpState.topics?.[it.topicId]?.properties
                                ?.name
                            }{' '}
                            {it.deleted && `(${messages.deleted})`}
                            {it.properties.corrective &&
                              `(${messages.corrective})`}
                          </span>
                          {selected && isAdmin && (
                            <ButtonGroup
                              items={classPages.reduce((current, item) => {
                                current[item.link] = item.title;
                                return current;
                              }, {})}
                              selected={page}
                              onSelect={(page) => {
                                const result = notifyUnsavedChanges?.() ?? true;
                                if (result) setPage(page);
                              }}
                              vertical={true}
                            />
                          )}
                        </Card>
                      </td>
                      {!clazz && (
                        <>
                          <td>{duration || '--'}</td>
                          <th>
                            {props.users[it.teacherUserId || 0]?.name ||
                              it.properties.teacher ||
                              '--'}
                          </th>
                          <th>
                            {holdType ? props.tags[holdType]?.text : '--'}
                          </th>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {props.isPDPAdmin && (
          <Input
            id={'showDeleted'}
            type="checkbox"
            name="showDeletedCheck"
            checked={showDeleted}
            label={messages.showDeleted}
            onChange={(e) => {
              setShowDeleted(e.target.checked);
            }}
          />
        )}
      </ClassListContainer>
      {clazz && props.pdpState.topics && (
        <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          {clazz ? (
            !isAdmin ? (
              <ClassViewer
                templateClass={clazz}
                company={props.company}
                tags={props.tags}
                pdpState={props.pdpState}
                relatedProviders={props.relatedProviders}
                thisUserId={Number(props.thisUser.userId)}
                users={props.users}
                setMyGroup={(classId, group) =>
                  props.setMyGroup(pdpID, classId, group)
                }
                canIFill={props.canIFill}
                pdpId={pdpID}
              />
            ) : page === 'edit' ? (
              <EditorWrapper
                style={{
                  flexGrow: '1',
                  height: '100%',
                  // overflow: 'scroll',
                  marginRight: 0,
                  border: `3px solid ${lightGreenSolid}`,
                }}
              >
                <ClassTemplateEditor
                  key={clazz.id}
                  topic={props.pdpState.topics[clazz.topicId]}
                  templateClass={clazz}
                  company={props.company}
                  tags={props.tags}
                  relatedProviders={props.relatedProviders}
                  saveClass={(it, skipConflictCheck) =>
                    props.createOrUpdateClass(it, pdpID, skipConflictCheck)
                  }
                  closeClass={() => {
                    navigate('../class');
                  }}
                  requireSeveralUser={props.requireSeveralUser}
                  companyMembers={props.companyMembers}
                  createProviderSurvey={props.createProviderSurvey}
                  updateSurvey={props.updateSurvey}
                  token={props.token}
                  terms={props.pdpState.terms}
                  pdpId={pdpID}
                />
                {/* {props.children} */}
              </EditorWrapper>
            ) : page === 'preview' ? (
              <ClassViewer
                templateClass={clazz}
                company={props.company}
                tags={props.tags}
                pdpState={props.pdpState}
                relatedProviders={props.relatedProviders}
                thisUserId={Number(props.thisUser.userId)}
                users={props.users}
                isAdmin={true}
                setMyGroup={(classId, group) =>
                  props.setMyGroup(pdpID, classId, group)
                }
                canIFill={props.canIFill}
                pdpId={pdpID}
              />
            ) : (
              <ClassMemberViewer
                key={clazz.id}
                clazz={clazz}
                users={props.users}
                pdpID={pdpID}
                tags={props.tags}
                company={props.company}
                pdpState={props.pdpState}
              />
            )
          ) : termId ? (
            <></>
          ) : (
            /*<ClassViewerWrapper>
              <ClassListView
                pdpState={props.pdpState}
                company={props.company}
                isPdpAdmin={props.isPDPAdmin}
                thisUserId={Number(props.thisUser.userId)}
                tags={props.tags}
                term={props.pdpState.terms?.[termId].term}
              />
            </ClassViewerWrapper>*/
            <LoadingCircleIndicator />
          )}
        </div>
      )}
    </TermsWrapper>
  );
};

const mapStateToProps: MapStateToProps<StateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const company = state.companies.data[Number(ownProps.companyId)];
  const selectedCompanyPdp = company.pdpPanels.find(
    (companyPdp) => companyPdp.id === Number(ownProps.pdpId)
  )!;
  const providerIds = Object.values(state.providers.map).reduce(
    (current: number[], it) => {
      if (it.data?.managingCompanyId === Number(ownProps.companyId))
        current.push(it.data.id);
      return current;
    },
    [] as number[]
  );
  let tags = {} as ITags['data'];
  // const toLoadTags = [] as number[];
  providerIds.forEach((id: number) => {
    // if (!state.providers.tags[id]?.status) toLoadTags.push(id);
    tags = {
      ...tags,
      ...state.providers.tags[id]?.data,
    };
  });
  const companyObj = company as ICompany;
  const relatedProviders =
    state.companies.data[Number(ownProps.companyId)].relatedProviders;
  const thisUserView = state.thisUser;
  const thisUser = thisUserView as IThisUser;
  const member = companyObj.members[Number(thisUser.userId)];
  const isPDPAdmin =
    isCompanyOwnerOrSupport(thisUserView, company) ||
    (!!selectedCompanyPdp?.managerRoleId &&
      !!member?.roles?.[selectedCompanyPdp?.managerRoleId]);

  const users = state.users.map;
  const companyMembers = state.companies.data[companyObj.id].members;
  const token = getAccessToken(state);

  return {
    company: companyObj,
    relatedProviders,
    tags: tags,
    pdpState: state.pdp.pdpMap[Number(ownProps.pdpId)!],
    isPDPAdmin,
    thisUser,
    companyMembers: companyMembers ?? undefined,
    users,
    token,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  IOwnProps
> = () => {
  return {
    queryClasses: (
      pdpId: number,
      startDate?: number,
      endDate?: number,
      isTemplate?: boolean,
      termId?: number,
      includeDeleted?: boolean
    ) =>
      appDispatch(
        queryClasses({
          pdpId,
          startDate,
          endDate,
          isTemplate,
          termId,
          includeDeleted,
        })
      ),
    createOrUpdateClass: (
      pdpClass: PDPClass,
      pdpId: number,
      skipConflictCheck
    ) =>
      appDispatch(
        createOrUpdateClass({ clazz: pdpClass, pdpId, skipConflictCheck })
      ),
    getClass: (classId: number, pdpId: number) =>
      appDispatch(getClass({ classId, pdpId })),
    getMyClasses: (termId: number, pdpId: number) =>
      appDispatch(getMyClasses({ pdpId, termId })),
    getMyManaginClasses: (termId: number, pdpId: number) =>
      appDispatch(getMyManagingClasses({ pdpId, termId })),
    updateParticipants: (pdpId: number, participants: Participant[]) =>
      appDispatch(updateParticipants({ pdpId, participants })),
    setMyGroup: (pdpId: number, clazzId: number, group: string) =>
      appDispatch(setMyGroup({ pdpId, clazzId, group })),

    requireSeveralUser: (usersList: number[]) =>
      appDispatch(requireSeveralUser({ usersList })),
    createProviderSurvey: (item: IProviderItemView) => {
      return appDispatch(item_createNewSurvey({ item }));
    },
    updateSurvey: (
      surveyData: ISurveyView,
      providerSurveyItemData: ISurveyItem
    ) => appDispatch(updateSurvey({ surveyData, providerSurveyItemData })),
    canIFill: (response: ISurveyResponseView, surveyItemData: ISurveyItem) =>
      appDispatch(canIFill({ response, surveyItemData })),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClassManager) as any;
